import { Box, Paper, Typography, Button, Divider } from '@mui/material';
import { Mail, Calendar, Clock, Zap } from 'lucide-react';
import { useState } from 'react';
import { render } from '@react-email/render';
import { CollectionRequest, ResponseHandling } from '@lingaai/shared-utils';
import type { Fund } from '@lingaai/types';
import { usePortfolioCompanies, useMetricDefinitions } from '@lingaai/shared-hooks';
import { EmailPreviewDrawer } from './EmailPreviewDrawer';
import { getCurrentPeriod, calculateEmailSchedule, calculateDeadline, getPeriodDateRange, formatDate, daysUntil } from '../utils/emailSchedule';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';

interface SubmissionsEmptyStateProps {
  fund: Fund;
}

export function SubmissionsEmptyState({ fund }: SubmissionsEmptyStateProps) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');

  const currentPeriod = fund.reporting_frequency ? getCurrentPeriod(fund.reporting_frequency, fund.reporting_timezone || 'UTC') : null;

  const { data: portfolioCompanies = [] } = usePortfolioCompanies(fund.id);
  const { data: metricDefinitions = [] } = useMetricDefinitions(fund.id);

  if (!currentPeriod || !fund.reporting_frequency) {
    return null;
  }

  const deadline = calculateDeadline(currentPeriod, fund.reporting_frequency, fund.reporting_day_of_month || 15);

  const dateRange = getPeriodDateRange(currentPeriod, fund.reporting_frequency);
  const emailSchedule = calculateEmailSchedule(currentPeriod, fund.reporting_frequency, fund.reporting_day_of_month || 15);

  const daysUntilDeadline = daysUntil(deadline);

  const handlePreviewEmail = async (type: 'request' | 'acknowledgment' | 'followup' | 'summary', reminderCount?: number) => {
    const sampleCompany = portfolioCompanies[0]?.name || 'Sample Company';
    const metricsList = metricDefinitions.filter((m) => m.is_required).map((m) => m.name);
    const defaultMetrics = metricsList.length > 0 ? metricsList : ['Revenue', 'EBITDA', 'Customer Count'];

    let html: string;
    let subject: string;

    if (type === 'request') {
      html = await render(<CollectionRequest contactName="John Doe" companyName={sampleCompany} period={currentPeriod} reminderCount={reminderCount || 0} metricsList={defaultMetrics} />);
      subject = reminderCount === 0 ? `${currentPeriod} Metrics Request — ${sampleCompany}` : `Reminder ${reminderCount}: ${currentPeriod} Metrics Request — ${sampleCompany}`;
    } else if (type === 'acknowledgment') {
      html = await render(<ResponseHandling contactName="John Doe" companyName={sampleCompany} period={currentPeriod} status="received" metricsSummary={defaultMetrics} />);
      subject = `Received — ${currentPeriod} metrics for ${sampleCompany}`;
    } else if (type === 'followup') {
      html = await render(<ResponseHandling contactName="John Doe" companyName={sampleCompany} period={currentPeriod} status="missing_data" missingMetrics={['Customer Count', 'Burn Rate']} />);
      subject = `Additional information needed — ${currentPeriod} update`;
    } else {
      html = await render(<ResponseHandling contactName="John Doe" companyName={sampleCompany} period={currentPeriod} status="complete" />);
      subject = `${currentPeriod} Quarterly Metrics — Complete`;
    }

    setPreviewHtml(html);
    setPreviewSubject(subject);
    setPreviewOpen(true);
  };

  return (
    <>
      <Box sx={{ py: 1 }}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Calendar size={24} strokeWidth={ICON_STROKE} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Next Submission Period: {currentPeriod}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {formatDate(dateRange.start)} — {formatDate(dateRange.end)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Deadline
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatDate(deadline)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {daysUntilDeadline > 0 ? `${daysUntilDeadline} days away` : daysUntilDeadline === 0 ? 'Today' : `${Math.abs(daysUntilDeadline)} days overdue`}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Portfolio Companies
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {portfolioCompanies.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    will be invited
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Mail size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Scheduled Emails
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Automated emails sent on a schedule
            </Typography>

            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: 23,
                  top: 8,
                  bottom: 8,
                  width: 2,
                  bgcolor: 'divider',
                }}
              />

              {emailSchedule.map((item) => (
                <Box key={item.type} sx={{ position: 'relative', pl: 6, pb: 2 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 14,
                      top: 2,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: item.reminderCount === 0 ? 'primary.main' : item.reminderCount === 3 ? 'warning.main' : 'grey.400',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                    }}
                  >
                    <Mail size={12} strokeWidth={2} />
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {item.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Clock size={10} strokeWidth={1} />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {formatDate(item.sendDate)} at 9:00 AM
                      </Typography>
                    </Box>
                    <Button size="small" variant="outlined" onClick={() => handlePreviewEmail('request', item.reminderCount)} sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}>
                      Preview
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Zap size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Event-Triggered Emails
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sent automatically when events occur
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Acknowledgment Email
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Sent when portfolio company submits data
                </Typography>
                <Button size="small" variant="outlined" onClick={() => handlePreviewEmail('acknowledgment')} sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}>
                  Preview
                </Button>
              </Box>

              <Divider />

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Follow-up Request
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Sent when data is incomplete or missing
                </Typography>
                <Button size="small" variant="outlined" onClick={() => handlePreviewEmail('followup')} sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}>
                  Preview
                </Button>
              </Box>

              <Divider />

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Completion Summary
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Sent when all metrics are collected
                </Typography>
                <Button size="small" variant="outlined" onClick={() => handlePreviewEmail('summary')} sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}>
                  Preview
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      <EmailPreviewDrawer open={previewOpen} onClose={() => setPreviewOpen(false)} emailHtml={previewHtml} subject={previewSubject} />
    </>
  );
}
